import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  daIso, differenzaGiorni, giorniStriscia, GIORNI_BREVI, isoGiorno, MESI, MESI_TITOLO, perGiorno, titoloGiorno, weekend,
} from '../utils/agenda';
import { nomeDaIntestazione, salvaBlob } from '../utils/documenti';
import { testoEsitoCliente, conAnno } from '../utils/visite';
import { adessoRoma, attendeNuovoOrario, chiedeEsito, motivoVisitaBreve } from '../utils/percorso';
import { useTraduzioni, contesto, PRINCIPALE } from '../i18n';
import Icona from '../components/cliente/Icone';
import { Sposta } from '../components/cliente/CardOggi';
import { useConsulente } from '../components/cliente/useConsulente';
import Disponibilita from '../components/Disponibilita';
import '../components/staff/staff.css';
import '../components/cliente/calendario.css';

// /calendario del cliente: la bozza design/agenda-v9.html (vista cliente). Striscia dei giorni e la sua giornata,
// Prossimi (da oggi, con che cosa è e il mese), Già fatte con il suo esito, .ics per ogni visita e per tutto, Sposta
// e la nota per il cliente sulle visite in programma, la disponibilità per le visite.
// Dati: GET /api/calendario/mio (backend/agenda.py): solo le sue visite e le date della sua pratica.

const TIPI_VOCE = ['visita', 'scadenza', 'rogito'];
// il giorno della settimana in breve e il mese in maiuscolo, nella lingua del cliente
const giornoBreve = (d, lingua) => (lingua === PRINCIPALE ? GIORNI_BREVI[d.getDay()]
  : contesto(lingua).data(d, { weekday: 'short', timeZone: undefined }));
const meseTitolo = (d, lingua) => {
  if (lingua === PRINCIPALE) return MESI_TITOLO[d.getMonth()];
  const nome = contesto(lingua).data(d, { month: 'long', timeZone: undefined });
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)}`;
};
// il mese breve nella lingua del cliente ("gen", "янв.")
const meseBreve = (d, lingua) => (lingua === PRINCIPALE ? MESI[d.getMonth()].slice(0, 3)
  : contesto(lingua).data(d, { month: 'short', timeZone: undefined }));

// Il motivo di un errore anche quando la risposta è un blob (download del .ics)
const testoDelBlob = (blob) => (typeof blob.text === 'function' ? blob.text() : new Promise((ok, no) => {
  const lettore = new FileReader();
  lettore.onload = () => ok(lettore.result);
  lettore.onerror = no;
  lettore.readAsText(blob);
}));
const dettaglioErrore = async (e) => {
  const dati = e?.response?.data;
  try {
    const testo = dati instanceof Blob ? await testoDelBlob(dati) : null;
    const detail = testo ? JSON.parse(testo).detail : dati?.detail;
    return typeof detail === 'string' ? detail : null;
  } catch { return null; }
};

// Il nome del file è quello del server (Content-Disposition, come lo zip dei documenti: "visita-3f2a9c1e.ics"); `nome`
// solo se manca. Prima ogni visita si scaricava come "visita.ics" e il telefono le chiamava "visita (1).ics"…
// onRifiuto(messaggio): per la visita, un rifiuto del server (410 visita annullata nel frattempo, 404, 400) si dice e
// la pagina rilegge il calendario, invece di un errore lasciato lì con la visita ancora a video.
const scaricaIcs = async (url, nome, onRifiuto, t) => {
  try {
    const r = await axios.get(url, { responseType: 'blob' });
    const intestazioni = r.headers || {};
    const disposizione = typeof intestazioni.get === 'function' ? intestazioni.get('content-disposition') : intestazioni['content-disposition'];
    salvaBlob(r.data, nomeDaIntestazione(disposizione) || nome);
  } catch (e) {
    const stato = e?.response?.status;
    const motivo = await dettaglioErrore(e);
    if (onRifiuto && stato >= 400 && stato < 500 && stato !== 401) {
      onRifiuto(motivo ? `${motivo.replace(/\.$/, '')}.` : t('calendario.visita_cambiata'));
    } else if (stato === 404 && motivo) toast(motivo); // niente in programma: lo si dice, niente file vuoto
    else toast.error(motivo || t('calendario.ics_errore'));
  }
};

// Un file alla volta: con il doppio clic partivano due download (due file uguali nel telefono). Il blocco è subito
// (useRef, prima che React ridisegni) e il bottone resta spento finché il file non è pronto.
export const useScaricaIcs = () => {
  const { t } = useTraduzioni();
  const inCorso = useRef(false);
  const [attesa, setAttesa] = useState(false);
  const scarica = useCallback(async (url, nome, onRifiuto) => {
    if (inCorso.current) return;
    inCorso.current = true;
    setAttesa(true);
    try { await scaricaIcs(url, nome, onRifiuto, t); } finally { inCorso.current = false; setAttesa(false); }
  }, [t]);
  return { scarica, attesa };
};

const indicazioni = (imm) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([imm?.indirizzo, imm?.citta].filter(Boolean).join(', ') || imm?.titolo || '')}`;

// L'ora di Roma, come le voci del calendario (anche con il browser su un altro fuso: prima si confrontava l'ora di
// Roma dei dati con l'orologio del browser e la visita risultava passata o in programma all'ora sbagliata)
const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const oraAdesso = () => hhmm(adessoRoma());
const OGNI_MEZZO_MINUTO = 30 * 1000;
const PROSSIMI_VISIBILI = 6;
const ALTEZZA_BARRA = 66; // la barra in alto (cliente.css, .nav-cliente): quello che sta sotto non si vede
const movimento = () => (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

// Passata: un giorno prima di oggi, o oggi con l'ora già arrivata (come il server: alle 16:00 la visita delle 16:00
// non si sposta più). Senza ora (perizia, scadenze) vale tutto il giorno.
export const passata = (v, oggi, ora = oraAdesso()) => v.data < oggi || (v.data === oggi && !!v.ora && v.ora <= ora);

// In programma: da oggi compreso (di oggi, quelli non ancora passati), non annullati, visite senza esito.
// quante = null: tutti (con più di 6 appuntamenti "Prossimi" ha "Tutti (N) →", come "Già fatte")
export const vociProssime = (voci, oggi, ora = oraAdesso(), quante = null) => {
  const tutte = (voci || []).filter((v) => {
    if (v.stato === 'annullata' || (v.tipo === 'visita' && (v.esito || v.stato === 'fatta'))) return false;
    return !passata(v, oggi, ora);
  });
  return quante == null ? tutte : tutte.slice(0, quante);
};

// La voce del calendario come la visita che leggono le regole della home (chiedeEsito di utils/percorso.js)
const comeVisita = (v) => ({ id: v.visita_id, immobile_id: v.immobile?.id, data_visita: `${v.data}T${v.ora || '00:00'}:00`,
  esito_visita: v.esito || null, spostamento_richiesto: v.spostamento_richiesto || null });

// Senza esito: "Diteci com'è andata" solo se la home lo chiede davvero (stessa regola di esitiInSospeso: ultimi 30
// giorni, casa non scartata, nessuno spostamento chiesto); prima il link portava a una home senza la card.
// scartati = null se l'elenco delle case non è arrivato: allora niente link (non si sa se la home lo chiede).
export const esitoSenzaRisposta = (v, adesso, scartati) => {
  const visita = comeVisita(v);
  if (attendeNuovoOrario(visita, adesso)) return 'spostamento';
  if (!chiedeEsito(visita, adesso)) return 'non_dato';
  if (!scartati) return 'in_attesa';
  return scartati.has(visita.immobile_id) ? 'non_dato' : 'chiedi';
};

// Già fatte: le visite con l'esito, fatte, o con l'ora passata (anche quella di oggi, senza ricaricare la pagina)
export const visiteFatte = (voci, oggi, ora = oraAdesso()) => (voci || [])
  .filter((v) => v.tipo === 'visita' && v.stato !== 'annullata' && (v.esito || v.stato === 'fatta' || passata(v, oggi, ora)))
  .reverse();

// L'esito con le stesse parole della card e della scheda (TESTI_ESITO di utils/visite.js): "ci piace",
// "non fa per voi · prezzo", "in attesa del vostro parere"
const Esito = ({ v, adesso, scartati }) => {
  const { t, tp, l } = useTraduzioni();
  if (v.esito === 'positivo') return t('calendario.esito_vostro', { esito: testoEsitoCliente('positivo', l) });
  if (v.esito === 'negativo') {
    const motivi = (v.motivi || []).map((m) => motivoVisitaBreve(m, l) || m).join(', ');
    return t('calendario.esito_vostro', { esito: `${testoEsitoCliente('negativo', l)}${motivi ? ` · ${motivi}` : ''}` });
  }
  const senza = esitoSenzaRisposta(v, adesso, scartati);
  if (senza === 'spostamento') return t('calendario.esito_spostamento');
  if (senza === 'non_dato') return t('calendario.esito_non_dato');
  if (senza === 'in_attesa') return t('calendario.esito_attesa', { esito: testoEsitoCliente(null, l) });
  return <>{t('calendario.esito_attesa', { esito: testoEsitoCliente(null, l) })} · <Link className="link" to="/dashboard">{t('calendario.diteci')}</Link></>;
};

// Chi c'è all'appuntamento: "con Sandro" solo per le visite (per scadenze, perizia e rogito `con` è chi dello staff
// le segue, non uno che viene); per perizia e delibera la banca, per il rogito il luogo
const DOVE_BANCA = /banca|perizia/i;
const sottotitolo = (v, inProgramma, banca, t) => [
  v.tipo === 'visita' && v.con ? t('calendario.con_chi', { nome: v.con }) : null,
  v.tipo !== 'visita' && DOVE_BANCA.test(v.titolo || '') && banca ? t('calendario.banca', { nome: banca }) : null,
  inProgramma ? t('calendario.documento_identita') : null,
  v.dettaglio?.luogo,
].filter(Boolean).join(' · ');

// consulente: quello vero del cliente (useConsulente, come la home): lo spostamento lo gestisce lui. Prima "Sposta"
// diceva "Ernesto vi propone un nuovo orario" (chi accompagna, v.con) e con un 500 mancava il link WhatsApp (senza numero)
const CardVisita = ({ v, oggi, ora, adesso, scartati, banca, consulente, onAggiorna, onRifiuto }) => {
  const { t } = useTraduzioni();
  const [sposta, setSposta] = useState(false);
  const [chiesto, setChiesto] = useState(false);
  const ics = useScaricaIcs();
  // passata l'ora niente Sposta né "Nel mio calendario": il server risponderebbe "non si può più spostare"
  const inProgramma = v.tipo === 'visita' && !v.esito && v.stato !== 'annullata' && v.stato !== 'fatta' && !passata(v, oggi, ora);
  const fatta = v.tipo === 'visita' && !inProgramma && v.stato !== 'annullata';
  const richiesta = chiesto || v.spostamento_richiesto;
  return (
    <div className="ev hi" data-testid={`cal-${v.id}`}>
      <div className="corpo">
        {v.immobile?.foto && <img className="foto" src={v.immobile.foto} alt="" />}
        <div style={{ minWidth: 0 }}>
          <div className={`type ${v.tipo}`}>{v.tipo === 'visita' ? t('calendario.tipi.visita') : v.titolo}</div>
          <h3>{v.tipo === 'visita' ? v.titolo : (v.immobile?.titolo || v.titolo)}</h3>
          <p>{sottotitolo(v, inProgramma, banca, t)}</p>
          {fatta && <p className="esito-cal" data-testid="esito-visita"><Esito v={v} adesso={adesso} scartati={scartati} /></p>}
        </div>
      </div>
      {inProgramma && (
        <div className="acts">
          <a className="btn sm p" href={indicazioni(v.immobile)} target="_blank" rel="noopener noreferrer">{t('comune.indicazioni')}</a>
          <button type="button" className="btn sm" onClick={() => setSposta(!sposta)} disabled={richiesta} aria-expanded={sposta}>
            {t(richiesta ? 'comune.spostamento_chiesto' : 'comune.sposta')}
          </button>
          <button type="button" className={`btn sm${ics.attesa ? ' busy' : ''}`} aria-busy={ics.attesa} disabled={ics.attesa}
            onClick={() => ics.scarica(`${API}/visite/${v.visita_id}/ics`, 'visita.ics', onRifiuto)}>
            <Icona nome="calendario" />{t('calendario.nel_calendario')}
          </button>
        </div>
      )}
      {v.nota && inProgramma && (
        <div className="nota-cliente" data-testid="nota-visita">
          <small>{v.con ? t('calendario.nota_di_per_voi', { nome: v.con }) : t('calendario.nota_per_voi')}</small>
          <p>“{v.nota}”</p>
        </div>
      )}
      {sposta && !richiesta && inProgramma && (
        <div className="sposta-cal">
          <Sposta visita={{ id: v.visita_id }} consulente={consulente}
            onRichiesta={() => { setChiesto(true); setSposta(false); if (onAggiorna) onAggiorna(); }}
            onRifiuto={(messaggio) => { setSposta(false); if (onRifiuto) onRifiuto(messaggio); }} />
        </div>
      )}
      {richiesta && inProgramma && (
        <p className="sposta-fatto"><b>{t('home.oggi.richiesta_inviata')}</b> {t('home.oggi.propone_orario', { nome: consulente.nome })}</p>
      )}
    </div>
  );
};

// Il ritorno da Google (il server porta a /il-mio-calendario?google_connected=true o ?google_error=…; App.js passa
// l'indirizzo a /calendario col parametro, prima si perdeva e la pagina non diceva niente)
export const esitoGoogleDa = (search, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  let p;
  try { p = new URLSearchParams(search || ''); } catch { return null; }
  if (p.get('google_connected') === 'true') return { ok: true, testo: t('calendario.google_collegato') };
  const errore = p.get('google_error');
  if (!errore) return null;
  return { ok: false, testo: t(errore === 'invalid_state' ? 'calendario.google_stato_scaduto' : 'calendario.google_non_confermato') };
};

// Errori di Google Calendar: il testo del server per le risposte 4xx (per esempio «si sta già aggiornando da
// un'altra pagina»); con un 5xx (anche in testo semplice) o senza rete, il ripiego in parole
export const GOOGLE_NON_AGGIORNATO = 'Non siamo riusciti ad aggiornare Google Calendar: riprovate tra poco.';
export const GOOGLE_NON_SCOLLEGATO = 'Non siamo riusciti a scollegare Google Calendar: riprovate tra poco.';
export const GOOGLE_NON_APERTO = 'Non siamo riusciti ad aprire Google: riprovate tra poco.';
export const erroreGoogle = (e, ripiego = GOOGLE_NON_AGGIORNATO) => {  // il ripiego lo passa la pagina, già tradotto
  const dettaglio = e?.response?.data?.detail;
  return e?.response && e.response.status < 500 && typeof dettaglio === 'string' && dettaglio.trim() ? dettaglio : ripiego;
};

// Google Calendar (facoltativo, come prima): collega, sincronizza, scollega. esito: il ritorno da Google (sopra)
const GoogleCalendar = ({ esito }) => {
  const { t } = useTraduzioni();
  const [stato, setStato] = useState(null);
  const [attesa, setAttesa] = useState(false);
  useEffect(() => { axios.get(`${API}/auth/google/client/status`).then((r) => setStato(r.data)).catch(() => setStato(null)); }, []);
  if (!stato) return esito ? <p className={`muto${esito.ok ? '' : ' errore-google'}`} style={{ fontSize: '.78rem' }} role="status" data-testid="esito-google">{esito.testo}</p> : null;
  // ripiego: il messaggio quando il server non dice niente di utile (500 in testo semplice, rete assente); prima «Non riuscito»
  const azione = async (fn, ripiego) => {
    setAttesa(true);
    try { await fn(); } catch (e) { toast.error(erroreGoogle(e, ripiego)); } finally { setAttesa(false); }
  };
  return (
    <p className="muto" style={{ fontSize: '.78rem' }} data-testid="google-calendar-card">
      {esito && <span className={esito.ok ? undefined : 'errore-google'} role="status" data-testid="esito-google" style={{ display: 'block', marginBottom: '.3rem' }}>{esito.testo}</span>}
      {stato.connected
        ? <>{stato.email ? t('calendario.google_collegato_con', { email: stato.email }) : t('calendario.google_collegato_senza')}</>
        : <>{t('calendario.google_usate')}</>}
      {stato.connected ? (
        <>
          <button type="button" className="link" disabled={attesa} onClick={() => azione(async () => {
            const r = await axios.post(`${API}/auth/google/client/sync`);
            (r.data?.errors ? toast.error : toast.success)(r.data?.message || t('calendario.google_aggiornato'));
          }, t('calendario.google_non_aggiornato'))}>{t('calendario.google_sincronizza')}</button>
          {' · '}
          <button type="button" className="link" disabled={attesa} onClick={() => azione(async () => { await axios.post(`${API}/auth/google/client/disconnect`); setStato({ connected: false }); }, t('calendario.google_non_scollegato'))}>{t('calendario.google_scollega')}</button>
        </>
      ) : (
        <button type="button" className="link" disabled={attesa} onClick={() => azione(async () => { const r = await axios.get(`${API}/auth/google/client/login`); window.location.href = r.data.authorization_url; }, t('calendario.google_non_aperto'))}>{t('calendario.google_collegatelo')}</button>
      )}
    </p>
  );
};

const CalendarioCliente = () => {
  const { user } = useAuth();
  const { t, tp, l } = useTraduzioni();
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [giorno, setGiorno] = useState(null);
  const [tutteFatte, setTutteFatte] = useState(false);
  const [tuttiProssimi, setTuttiProssimi] = useState(false);
  // le case (per sapere quali sono scartate: lì la home non chiede l'esito) e le banche delle perizie; se non
  // arrivano il calendario si vede lo stesso, senza "Diteci com'è andata" e senza il nome della banca
  const [contorno, setContorno] = useState({ scartati: null, banche: {} });
  // l'ora di Roma che avanza: passata l'ora della visita di oggi, va tra le "Già fatte" senza ricaricare; tornando
  // sulla pagina (telefono ripreso in mano, timer fermi in sottofondo) si aggiorna subito
  const [adesso, setAdesso] = useState(adessoRoma);
  useEffect(() => {
    const t = setInterval(() => setAdesso(adessoRoma()), OGNI_MEZZO_MINUTO);
    const visibile = () => { if (document.visibilityState !== 'hidden') setAdesso(adessoRoma()); };
    document.addEventListener('visibilitychange', visibile);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', visibile); };
  }, []);
  const ora = hhmm(adesso);
  // "Oggi" dall'orologio (ora di Roma), non dal giorno letto all'apertura (dati.oggi): lasciata aperta oltre la
  // mezzanotte, la pagina teneva la visita di ieri "in programma" con Sposta e il titolo "Oggi, martedì 15"
  const oggi = isoGiorno(adesso);
  const tutto = useScaricaIcs();
  const { hash, search } = useLocation();
  const navigate = useNavigate();
  const consulente = useConsulente(user?.id);
  useEffect(() => {
    const precedente = document.title;
    document.title = `MK Client — ${t('calendario.titolo_pagina')}`; // prima restava "MK Client"
    return () => { document.title = precedente; };
  }, []);
  // Ritorno da Google: si dice com'è andata (anche con un avviso: a 390 px la riga di Google Calendar è in fondo) e
  // l'indirizzo torna pulito, così ricaricando non lo si ridice
  const [esitoGoogle] = useState(() => esitoGoogleDa(search, l));
  useEffect(() => {
    if (!esitoGoogle) return;
    (esitoGoogle.ok ? toast.success : toast.error)(esitoGoogle.testo);
    navigate({ search: '', hash }, { replace: true });
  }, []); // eslint-disable-line
  const intestazioneGiorno = useRef(null);
  const rifStriscia = useRef(null);
  const carica = useCallback((primaVolta = false) => {
    setErrore(false);
    const base = user?.id ? `${API}/clienti/${encodeURIComponent(user.id)}` : null;
    if (base) {
      Promise.allSettled([axios.get(`${base}/immobili`), axios.get(`${base}/proposte`)]).then(([immobili, proposte]) => {
        const elenco = (r) => (r.status === 'fulfilled' && Array.isArray(r.value?.data) ? r.value.data : null);
        const case_ = elenco(immobili);
        const banche = {};
        (elenco(proposte) || []).forEach((p) => { if (p.mutuo_banca && p.immobile_id) banche[p.immobile_id] = p.mutuo_banca; });
        setContorno({ scartati: case_ ? new Set(case_.filter((i) => i.stato_attivita === 'scartato').map((i) => i.id)) : null, banche });
      });
    }
    return axios.get(`${API}/calendario/mio`)
      .then((r) => { setDati(r.data); setAdesso(adessoRoma()); if (primaVolta) setGiorno((g) => g || isoGiorno(adessoRoma())); })
      .catch(() => setErrore(true));
  }, [user?.id]);
  useEffect(() => { carica(true); }, [carica]);
  // Cambio di giorno a pagina aperta: il giorno scelto, se era "oggi", passa al nuovo oggi e il calendario si rilegge
  const oggiDiPrima = useRef(oggi);
  useEffect(() => {
    const prima = oggiDiPrima.current;
    if (prima === oggi) return;
    oggiDiPrima.current = oggi;
    setGiorno((g) => (g === prima ? oggi : g));
    carica();
  }, [oggi, carica]);
  // "Sposta" o "Nel mio calendario" rifiutati dal server (visita annullata nel frattempo): si dice perché e si rilegge,
  // anche la griglia della disponibilità (prima restava la cella "visita già fissata" della visita annullata)
  const [giroGriglia, setGiroGriglia] = useState(0);
  const rifiutata = useCallback((messaggio) => {
    toast(`${messaggio} ${t('calendario.aggiornato')}`);
    carica();
    setGiroGriglia((n) => n + 1);
  }, [carica, t]);
  // /calendario#disponibilita (le notifiche «Le vostre disponibilità» mandate prima): la sezione è in fondo e compare
  // solo a calendario caricato, quindi il browser non ci arrivava da solo (a 390 px restava sotto la barra). Si scorre
  // a calendario pronto e di nuovo quando arriva la griglia (prima è una riga: la pagina poteva non avere dove scorrere)
  const pronto = Boolean(dati && giorno);
  const [grigliaPronta, setGrigliaPronta] = useState(false);
  const segnaGriglia = useCallback(() => setGrigliaPronta(true), []);
  useEffect(() => {
    if (!pronto || !hash || hash.length < 2) return undefined;
    const quadro = requestAnimationFrame(() => {
      let el = null;
      try { el = document.getElementById(decodeURIComponent(hash.slice(1))); } catch { el = null; }
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: movimento(), block: 'start' });
    });
    return () => cancelAnimationFrame(quadro);
  }, [pronto, grigliaPronta, hash]);
  // Toccando una voce di "Prossimi": la card del giorno e il giorno nella striscia in vista (a 390 px la card sta sopra,
  // fuori dallo schermo, e il giorno nella striscia oltre il bordo: sembrava che il tocco non facesse niente)
  const [richiestaVista, setRichiestaVista] = useState(0); // anche toccando di nuovo il giorno già scelto
  const scegliDaProssimi = (data) => { setGiorno(data); setRichiestaVista((n) => n + 1); };
  useEffect(() => {
    if (!richiestaVista) return;
    const barra = rifStriscia.current;
    const scelto = barra?.querySelector('[aria-selected="true"]');
    if (barra && scelto) {
      const b = barra.getBoundingClientRect();
      const s = scelto.getBoundingClientRect();
      if (s.left < b.left || s.right > b.right) barra.scrollLeft += (s.left - b.left) - (b.width - s.width) / 2;
    }
    const testa = intestazioneGiorno.current;
    if (testa && typeof testa.scrollIntoView === 'function') {
      const r = testa.getBoundingClientRect();
      if (r.top < ALTEZZA_BARRA || r.top > window.innerHeight * 0.6) testa.scrollIntoView({ behavior: movimento(), block: 'start' });
    }
  }, [richiestaVista]);
  if (errore && !dati) {
    return (
      <main className="st wrap" data-testid="calendario-errore">
        <div className="top r"><div><div className="k">{t('calendario.occhiello')}</div><h1>{meseTitolo(adesso, l)} <em>{adesso.getFullYear()}</em></h1></div></div>
        <section className="card r errore-cal" role="alert">
          <p>{t('calendario.errore')}</p>
          <button type="button" className="btn p" onClick={() => carica(true)}>{t('comune.riprova')}</button>
        </section>
      </main>
    );
  }
  if (!dati || !giorno) return <main className="st wrap"><p className="vuoto">{t('calendario.carico')}</p></main>;

  const per = perGiorno(dati.voci);
  // da ieri a +12; un giorno scelto fuori (una voce di "Prossimi" oltre le due settimane, o un giorno rimasto scelto
  // dopo la mezzanotte) allunga la striscia fino a lui, così resta segnato
  const primaDiOggi = Math.max(1, differenzaGiorni(oggi, giorno));
  const striscia = giorniStriscia(oggi, primaDiOggi, Math.max(13 + primaDiOggi, primaDiOggi + differenzaGiorni(giorno, oggi) + 3));
  const delGiorno = per[giorno] || [];
  const tuttiIProssimi = vociProssime(dati.voci, oggi, ora);
  const prossimi = tuttiProssimi ? tuttiIProssimi : tuttiIProssimi.slice(0, PROSSIMI_VISIBILI);
  const giaFatte = visiteFatte(dati.voci, oggi, ora);
  const fatte = tutteFatte ? giaFatte : giaFatte.slice(0, 6);
  const bancaDi = (v) => v.dettaglio?.banca || contorno.banche[v.immobile?.id] || null;
  const d = daIso(giorno);
  const titoloDelGiorno = titoloGiorno(giorno, oggi, l);
  // "Martedì 12 gennaio 2027" per un giorno di un altro anno (conAnno); "Oggi, …", "Ieri, …" e "Domani, …" restano
  // come sono (sono di questi giorni e non portano il mese)
  const titolo = titoloDelGiorno.prefisso ? titoloDelGiorno : { ...titoloDelGiorno, resto: conAnno(titoloDelGiorno.resto, d, adesso) };
  return (
    <main className="st wrap" data-testid="calendario-cliente">
      <div className="top r">
        <div><div className="k">{t('calendario.occhiello')}</div><h1>{meseTitolo(d, l)} <em>{d.getFullYear()}</em></h1></div>
        <button type="button" className={`btn${tutto.attesa ? ' busy' : ''}`} aria-busy={tutto.attesa} disabled={tutto.attesa}
          onClick={() => tutto.scarica(`${API}/calendario/mio.ics`, 'mk-consulting-calendario.ics')}>{t('calendario.aggiungi_telefono')}</button>
      </div>
      <div className="strip r" role="tablist" aria-label={t('calendario.giorni')} ref={rifStriscia}>
        {striscia.map((g) => {
          const dd = daIso(g);
          const tipi = [...new Set((per[g] || []).map((v) => v.tipo))];
          return (
            <button type="button" role="tab" key={g} aria-selected={g === giorno} className={`dp${g === giorno ? ' on' : ''}${weekend(g) ? ' we' : ''}`} onClick={() => setGiorno(g)}>
              <small>{giornoBreve(dd, l)}</small><b>{dd.getDate()}</b><i>{tipi.map((t) => <u key={t} className={`c-${t}`} />)}</i>
            </button>
          );
        })}
      </div>
      <div className="two">
        <div>
          <div className="dayh giorno-scelto r" ref={intestazioneGiorno} data-testid="giorno-scelto"><h2>{titolo.prefisso}<em>{titolo.resto}</em></h2><span>{delGiorno.length ? tp('comune.plurali.appuntamenti', delGiorno.length) : t('calendario.niente_in_programma')}</span></div>
          <div className="tl r">
            {delGiorno.length === 0 && <p className="vuoto" style={{ padding: '1rem 0' }}>{t('calendario.niente_questo_giorno')}</p>}
            {delGiorno.map((v) => (
              <div key={v.id} className={`slot ${v.tipo}`}>
                <div className={`h${v.ora ? '' : ' giorno'}`}>{v.ora || t('calendario.giorno_intero')}</div>
                <CardVisita v={v} oggi={oggi} ora={ora} adesso={adesso} scartati={contorno.scartati} banca={bancaDi(v)}
                  consulente={consulente} onAggiorna={() => carica()} onRifiuto={rifiutata} />
              </div>
            ))}
          </div>
        </div>
        <div className="stack">
          <section className="card r" data-testid="prossimi">
            <div className="ttl">
              <div className="i"><Icona nome="freccia" /></div><h2>{t('calendario.prossimi')}</h2>
              {/* oltre 6 appuntamenti gli altri non si vedevano (la striscia arriva a 12 giorni): come "Già fatte" */}
              {tuttiIProssimi.length > PROSSIMI_VISIBILI && (
                <button type="button" className="link" aria-expanded={tuttiProssimi} onClick={() => setTuttiProssimi(!tuttiProssimi)}>
                  {tuttiProssimi ? t('comune.meno') : t('calendario.tutti_n', { n: tuttiIProssimi.length })}
                </button>
              )}
            </div>
            <div className="next">
              {prossimi.length === 0 && <p className="vuoto">{t('calendario.nessun_appuntamento')}</p>}
              {prossimi.map((v) => {
                const dd = daIso(v.data);
                const visita = v.tipo === 'visita';
                // la visita col nome della casa; perizia, rogito, scadenze col loro nome (la casa sotto)
                const nome = visita ? (v.immobile?.titolo || v.titolo) : v.titolo;
                const sotto = [v.data === oggi ? t('calendario.oggi') : giornoBreve(dd, l), v.ora, visita ? t('calendario.visita') : null,
                  visita ? (v.con ? t('calendario.con_chi', { nome: v.con }) : null) : (v.immobile?.titolo || v.immobile?.indirizzo),
                  !visita && DOVE_BANCA.test(v.titolo || '') && bancaDi(v) ? t('calendario.banca', { nome: bancaDi(v) }) : null].filter(Boolean).join(' · ');
                return (
                  <div key={v.id} data-testid={`prossimo-${v.id}`}>
                    {/* con l'anno se è di un altro anno (conAnno), come "Già fatte": prima "20 GEN" sembrava di quest'anno */}
                    <div className="d">{dd.getDate()}<small>{conAnno(meseBreve(dd, l), dd, adesso)}</small></div>
                    <i className={`dot c-${v.tipo}`} title={t(`calendario.tipi.${TIPI_VOCE.includes(v.tipo) ? v.tipo : 'altro'}`)} />
                    {v.immobile?.foto && <img className="mini" src={v.immobile.foto} alt="" loading="lazy" />}
                    <button type="button" style={{ background: 'none', border: 0, color: 'inherit', textAlign: 'left', cursor: 'pointer', minWidth: 0 }} onClick={() => scegliDaProssimi(v.data)}>
                      <b>{nome}</b>
                      <span>{sotto}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
          <section className="card r">
            <div className="ttl"><div className="i g"><Icona nome="si" /></div><h2>{t('calendario.gia_fatte')}</h2>{giaFatte.length > 6 && <button type="button" className="link" onClick={() => setTutteFatte(!tutteFatte)}>{tutteFatte ? t('comune.meno') : t('calendario.tutte_n', { n: giaFatte.length })}</button>}</div>
            <div className="next">
              {fatte.length === 0 && <p className="vuoto">{t('calendario.nessuna_visita_fatta')}</p>}
              {fatte.map((v) => (
                <div key={v.id} data-testid={`fatta-${v.id}`}>
                  <div className="d">{daIso(v.data).getDate()}<small>{conAnno(meseBreve(daIso(v.data), l), daIso(v.data), adesso)}</small></div>
                  <i className="dot" style={{ background: v.esito === 'positivo' ? 'var(--ok)' : 'var(--mute)' }} />
                  {v.immobile?.foto && <img className="mini" src={v.immobile.foto} alt="" loading="lazy" />}
                  <div style={{ minWidth: 0 }}>
                    <b>{v.immobile?.titolo || v.immobile?.indirizzo || v.titolo}</b>
                    <span>{v.data === oggi ? `${v.ora ? t('calendario.oggi_alle', { ora: v.ora }) : t('calendario.oggi')} · ` : ''}<Esito v={v} adesso={adesso} scartati={contorno.scartati} /></span>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section className="card r" id="disponibilita" style={{ borderColor: 'rgba(201,162,75,.35)' }}>
            {user?.id && <Disponibilita clienteId={user.id} onCaricata={segnaGriglia} rileggi={giroGriglia} />}
          </section>
          <GoogleCalendar esito={esitoGoogle} />
        </div>
      </div>
    </main>
  );
};

export default CalendarioCliente;
